import {type LoadableView} from '../../../state/views/types';
import {safeLocalStorage} from '../../../util/localStorage';

export const SENT_AT_STORAGE_KEY = 'wb_ws_analytics/panel_inventory/sent_at';
export const SEND_INTERVAL_MS = 24 * 60 * 60 * 1000;

/**
 * Identifies a workspace across loads. Keyed by name rather than view ID, which
 * a workspace only gets when it is first saved.
 */
export const getInventoryWorkspaceKey = (
  view: Pick<LoadableView, 'name' | 'entityName' | 'project'>
) =>
  [
    view.project?.entityName ?? view.entityName ?? '',
    view.project?.name ?? '',
    view.name,
  ].join('/');

/** Last send time per workspace key. Unreadable storage reads as empty. */
function readSentAt(): Record<string, unknown> {
  try {
    const parsed = JSON.parse(
      safeLocalStorage.getItem(SENT_AT_STORAGE_KEY) ?? '{}'
    );
    return typeof parsed === 'object' && parsed != null ? parsed : {};
  } catch {
    return {};
  }
}

const isWithinInterval = (sentAt: unknown, now: number) =>
  typeof sentAt === 'number' &&
  now >= sentAt &&
  now - sentAt < SEND_INTERVAL_MS;

export const wasInventorySentRecently = (key: string, now = Date.now()) =>
  isWithinInterval(readSentAt()[key], now);

/** Records a send, dropping expired entries so the map stays small. */
export function recordInventorySent(key: string, now = Date.now()) {
  const sentAt = Object.fromEntries(
    Object.entries(readSentAt()).filter(([, t]) => isWithinInterval(t, now))
  );
  sentAt[key] = now;
  safeLocalStorage.setItem(SENT_AT_STORAGE_KEY, JSON.stringify(sentAt));
}
